import { keepPreviousData } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";

import type { Column } from "@fronterahealth/frontera-ui-components";
import { Button, Table } from "@fronterahealth/frontera-ui-components";

import { type GetPromptsOutput, ListPromptsReportType, RegistryStatusEnum } from "@api/rest/types-and-hooks";
import { useBasecampOrganizationsRetrieve, useListPrompts } from "@api/rest/types-and-hooks";
import { OpenBulkPRDialog } from "@components/Prompts/OpenBulkPRDialog";
import { ReportTypeChips } from "@components/Prompts/ReportTypeTags";
import { GLOBAL_ORG_LABEL, GLOBAL_ORG_SENTINEL, REPORT_TYPE_OPTIONS } from "@components/Prompts/constants";
import { REPORT_TYPE_LABELS } from "@pages/Templates/constants";

export const PromptsTable: React.FC = () => {
  const navigate = useNavigate();
  const pageItemsDefault = 10;
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageItemsDefault);
  const [orgFilter, setOrgFilter] = useState("");
  const [reportTypeFilter, setReportTypeFilter] = useState("");
  // Bulk-PR selection state — keyed by prompt.key (unique) so it
  // survives pagination without holding onto stale references. Meta
  // carries name + registry_status so the dialog can route matched
  // prompts to the EDIT-bulk endpoint and missing ones to CREATE-bulk
  // without a second lookup.
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [selectedMeta, setSelectedMeta] = useState<
    Map<string, { name: string; registryStatus: "matched" | "missing" }>
  >(new Map());
  const [isBulkPRDialogOpen, setIsBulkPRDialogOpen] = useState(false);

  const [debouncedSearchTerm] = useDebounce(searchTerm, 200);

  const { data, isFetching, refetch } = useListPrompts(
    {
      search: debouncedSearchTerm,
      organization: orgFilter || undefined,
      report_type: (reportTypeFilter || undefined) as ListPromptsReportType | undefined,
      page,
      page_size: pageSize,
    },
    {
      query: {
        queryKey: ["prompts", debouncedSearchTerm, orgFilter, reportTypeFilter, page, pageSize],
        placeholderData: keepPreviousData,
      },
    },
  );

  const { data: orgsResponse } = useBasecampOrganizationsRetrieve({});
  const orgs = useMemo(
    () =>
      orgsResponse?.status === 200 ? (Array.isArray(orgsResponse.data) ? orgsResponse.data : [orgsResponse.data]) : [],
    [orgsResponse],
  );

  const orgFilterOptions = useMemo(
    () => [
      { value: "", label: "All orgs" },
      { value: GLOBAL_ORG_SENTINEL, label: "Global" },
      ...orgs.map((o) => ({ value: String(o.id), label: o.name })),
    ],
    [orgs],
  );

  const promptsData = data?.status === 200 ? data.data : undefined;
  const prompts = promptsData?.results ?? [];
  const totalItemCount = promptsData?.count ?? 0;
  const isFiltered = orgFilter !== "" || reportTypeFilter !== "";

  const handleRowClick = (prompt: GetPromptsOutput) => {
    navigate(`/prompts/${prompt.key}`);
  };

  const handlePageChange = (newPage: number, itemsPerPage?: number) => {
    if (itemsPerPage && itemsPerPage !== pageSize) {
      setPageSize(itemsPerPage);
      setPage(1);
    } else {
      setPage(newPage);
    }
  };

  const toggleSelected = (record: GetPromptsOutput) => {
    // ``registry_status`` isn't in the generated OpenAPI types yet
    // (added to the list serializer in this PR); cast to the widened
    // shape until the next codegen. Missing / unknown → treat as
    // "missing" so the dialog defaults to CREATE-bulk, which is what
    // the old behavior was.
    const status =
      (record as GetPromptsOutput & { registry_status?: "matched" | "missing" }).registry_status ?? "missing";
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(record.key)) next.delete(record.key);
      else next.add(record.key);
      return next;
    });
    setSelectedMeta((prev) => {
      const next = new Map(prev);
      if (next.has(record.key)) next.delete(record.key);
      else next.set(record.key, { name: record.name, registryStatus: status });
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedKeys(new Set());
    setSelectedMeta(new Map());
  };

  const columns: Column<GetPromptsOutput>[] = [
    {
      dataIndex: "id",
      key: "select",
      title: "",
      className: "w-[4%]",
      // Checkbox column for bulk-PR selection. ``stopPropagation`` so
      // the surrounding row click (which navigates to the detail page)
      // doesn't fire when the user is just toggling selection.
      // Disabled when the prompt has no draft — there's nothing to PR,
      // and the open-PR endpoint 400s on empty edits. ``has_draft`` was
      // added to the list serializer but isn't in the generated OpenAPI
      // types yet; cast to the widened shape until the next codegen.
      render: (_: unknown, record: GetPromptsOutput) => {
        const hasDraft = (record as GetPromptsOutput & { has_draft?: boolean }).has_draft === true;
        return (
          <input
            type="checkbox"
            checked={selectedKeys.has(record.key)}
            onChange={() => toggleSelected(record)}
            onClick={(e) => e.stopPropagation()}
            disabled={!hasDraft}
            title={hasDraft ? undefined : "No draft to PR — edit the prompt first"}
            className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
            aria-label={`Select ${record.name}`}
          />
        );
      },
    },
    {
      dataIndex: "name",
      key: "name",
      title: "Name",
      className: "text-text-primary w-[18%]",
      render: (value: unknown, record: GetPromptsOutput) => (
        <div className="flex items-center gap-x-2">
          <span className="text-text-primary truncate">{value as string}</span>
          {record.registry_status === RegistryStatusEnum.missing && (
            <span
              className="shrink-0 rounded-full border border-border-subtle px-2 py-0.5 text-xs text-text-tertiary"
              title="Its PR hasn't merged yet, so it can't be used in templates."
            >
              Not merged
            </span>
          )}
        </div>
      ),
    },
    {
      dataIndex: "key",
      key: "key",
      title: "Key",
      className: "text-text-secondary w-[18%]",
      render: (value: unknown) => <span className="truncate block">{(value as string) ?? ""}</span>,
    },
    {
      dataIndex: "organization",
      key: "organization",
      title: "Organization owner",
      className: "w-[14%]",
      render: (_value: unknown, record: GetPromptsOutput) => {
        const orgName = record.organization?.name;
        return orgName ? (
          <span className="text-text-secondary truncate block" title={orgName}>
            {orgName}
          </span>
        ) : (
          <span className="text-text-tertiary italic">{GLOBAL_ORG_LABEL}</span>
        );
      },
    },
    {
      dataIndex: "report_types",
      key: "report_types",
      title: "Report types",
      className: "w-[10%]",
      render: (_value: unknown, record: GetPromptsOutput) => <ReportTypeChips reportTypes={record.report_types} />,
    },
    {
      dataIndex: "response_schema",
      key: "response_schema",
      title: "Response Schema",
      className: "text-text-secondary w-[20%]",
      ellipsis: true,
      render: (value: unknown) => (
        <span className="truncate block" title={value ? JSON.stringify(value) : ""}>
          {value ? JSON.stringify(value) : ""}
        </span>
      ),
    },
    {
      dataIndex: "sample_output",
      key: "sample_output",
      title: "Sample Output",
      className: "text-text-secondary w-[20%]",
      ellipsis: true,
      render: (value: unknown) => (
        <span className="truncate block" title={(value as string) ?? ""}>
          {(value as string) ?? ""}
        </span>
      ),
    },
  ];

  return (
    <>
      <div className="mt-4 flex items-center gap-3">
        <select
          value={orgFilter}
          onChange={(e) => {
            setOrgFilter(e.target.value);
            setPage(1);
          }}
          className="rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 pr-8 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-500"
        >
          {orgFilterOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={reportTypeFilter}
          onChange={(e) => {
            setReportTypeFilter(e.target.value);
            setPage(1);
          }}
          className="rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 pr-8 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-500"
        >
          <option value="">All report types</option>
          {REPORT_TYPE_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {REPORT_TYPE_LABELS[t]}
            </option>
          ))}
          <option value={ListPromptsReportType.__untagged__}>Untagged</option>
        </select>
        {isFiltered && (
          <button
            type="button"
            className="text-sm text-text-secondary hover:text-text-primary underline"
            onClick={() => {
              setOrgFilter("");
              setReportTypeFilter("");
              setPage(1);
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="mt-2 flow-root bg-bg-surface w-full">
        <Table
          key={`${orgFilter}|${reportTypeFilter}`}
          onRowClick={handleRowClick}
          defaultItemsPerPage={pageItemsDefault}
          loading={isFetching}
          showPagination
          showSearch
          inputClassName="w-96"
          itemsPerPageOptions={[5, 10, 20, 50]}
          actions={[]}
          data={prompts}
          columns={columns}
          onPageChange={handlePageChange}
          searchTerm={searchTerm}
          setSearchTerm={(term) => {
            setSearchTerm(term);
            setPage(1);
          }}
          totalItemCount={totalItemCount}
        />
      </div>

      {/* Bulk-PR selection bar — floats bottom-center when any row is
          checked. Doesn't affect the table layout when empty. */}
      {selectedKeys.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
          <div className="flex items-center gap-4 rounded-lg bg-bg-inverse text-text-inverse shadow-lg px-4 py-2.5">
            <span className="text-sm">
              {selectedKeys.size} {selectedKeys.size === 1 ? "prompt" : "prompts"} selected
            </span>
            <button className="text-sm underline hover:opacity-80" onClick={clearSelection}>
              Clear
            </button>
            <Button text="Open PR for selected" appearance="primary" onClick={() => setIsBulkPRDialogOpen(true)} />
          </div>
        </div>
      )}

      <OpenBulkPRDialog
        isOpen={isBulkPRDialogOpen}
        onClose={() => setIsBulkPRDialogOpen(false)}
        selectedPrompts={Array.from(selectedMeta.entries()).map(([key, meta]) => ({
          key,
          name: meta.name,
          registryStatus: meta.registryStatus,
        }))}
        onSuccess={() => {
          // Clear selection + refetch so the newly-PR'd prompts refresh
          // whatever downstream state (draft indicators, etc.) would
          // change once the PRs merge.
          clearSelection();
          refetch();
        }}
      />
    </>
  );
};
